<template>
  <LoginContainer
    class="reset-form"
  >
    <div
      v-if="apiResponse"
      class="px-md absolute left-0 right-0 top-sm"
    >
      <OrNotification
        class="alert-response sm:max-w-[768px] mx-auto"
        :variant="apiResponse.type"
        @close="closeNotificiation"
      >
        {{ apiResponse.message }}
        <router-link
          v-if="apiResponse.linkText"
          class="alert-link !text-primary-dark"
          to="/logout"
        >
          {{ apiResponse.linkText }}
        </router-link>
      </OrNotification>
    </div>

    <div class="layout-row grow px-md sm:px-0 sm:justify-center md:justify-between max-w-[1044px]">
      <div
        v-if="isDesktop"
        class="flex h-[504px] w-[492px] justify-center items-center"
      >
        <OrAnimationLogo />
      </div>
      <div class="layout-column w-full sm:w-[343px] md:w-[352px] gap-xl">
        <OrLogo
          class="self-center md:hidden mb-md"
        />
        <div class="layout-column gap-md items-center md:items-stretch">
          <h2
            class="typography-display-small"
          >
            {{ $t('login.password_restore.resetPassword') }}
          </h2>
          <div class="typography-body-2-regular">
            {{ $t('login.password_restore.setYourNewPassword') }}
          </div>
        </div>

        <div class="layout-column gap-md">
          <OrInput
            ref="passwordInput"
            v-model="password"
            type="password"
            :readonly="apiResponse?.type === 'success'"
            :label="$t('login.password_restore.password')"
            :placeholder="$t('session_expire.Enter password')"
            :disabled="loading"
            :error="passwordInvalid"
            @blur="v$.password.$touch()"
            @keyup.enter="submit"
          />
          <div class="strength">
            <PasswordStatus
              :password="password"
              :password-policies="passwordPolicies"
              :dirty="passwordInvalid"
            />
          </div>
        </div>
        <OrButton
          class="sign-in w-full"
          color="primary"
          :disabled="apiResponse?.type === 'success'"
          :loading="loading"
          @click="submit"
        >
          {{ $t('login.password_restore.set') }}
        </OrButton>
      </div>
    </div>
  </LoginContainer>
</template>

<script lang="ts">
import {
  OrButtonV3 as OrButton,
  OrInputV3 as OrInput,
  OrNotificationV3 as OrNotification,
  useResponsive,
} from '@onereach/ui-components';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { defineComponent, ref, computed, onBeforeUnmount } from 'vue';

import passwordApi from '../api/password';
import OrAnimationLogo from '../components/OrAnimationLogo/OrAnimationLogo.vue';
import OrLogo from '../components/OrLogo';
import PasswordStatus from '../components/PasswordStatus.vue';
import config from '../config';
import LoginContainer from '../layouts/LoginContainer.vue';
import { ApiResponse } from '../types/OrNotifications';
import { checkRwcInterval } from '../utils/constans';
import { passwordPolicies } from '../utils/PasswordPolicies';

export default defineComponent({
  components: {
    LoginContainer,
    OrButton,
    OrInput,
    PasswordStatus,
    OrNotification,
    OrAnimationLogo,
    OrLogo,
  },
  setup() {
    const password = ref('');
    const loading = ref(false);
    const passwordReseted = ref(false);
    const apiResponse = ref <ApiResponse>(null);
    const { isMobile, isDesktop } = useResponsive();


    const closeNotificiation = () => {
      apiResponse.value = null;
    };

    const rules = computed(() => ({
      password: {
        ...passwordPolicies,
        required,
      },
    }));
    const v$ = useVuelidate(rules, { password });

    const passwordInvalid = computed(() => {
      return v$.value.password.$error;
    });

    const passwordInput = ref<InstanceType<typeof OrInput>| null>(null);

    onBeforeUnmount(() => {
      if (config.HELPER_BOT_ENABLED) {
      // Show chat on pages except login
        const chat = document.querySelector('.or-rwc') as HTMLElement;
        if (chat) {
          chat.style.display = 'block';
        }
      }
    });

    return {
      v$,
      password,
      loading,
      passwordPolicies,
      passwordReseted,
      apiResponse,
      closeNotificiation,
      passwordInvalid,
      passwordInput,
      isMobile,
      isDesktop,
    };
  },
  watch: {
    authorized(value) {
      if (value) {
        this.$router.push('/');
      }
    },
  },
  created() {
    const resetKey = this.$route.query.key;
    const resetEmail = this.$route.query.email;

    if (!resetKey || !resetEmail) {
      this.$router.push('/login');
    }

    if (config.HELPER_BOT_ENABLED) {
      // Hide chat on login page.
      const interval = setInterval(() => {
        const chat = document.querySelector('.or-rwc') as HTMLElement;
        if (chat) {
          chat.style.display = 'none';
          clearInterval(interval);
        }
      }, checkRwcInterval);
    }
  },
  methods: {
    async submit() {
      this.v$.password.$touch();

      if (this.passwordInvalid) {
        this.passwordInput?.focus();
        return;
      }

      const resetKey = this.$route.query.key;
      const resetEmail = this.$route.query.email;
      const { type: userType } = this.$route.query;
      this.loading = true;
      try {
        await passwordApi.endResetPassword({
          email: resetEmail as string,
          key: resetKey as string,
          password: this.password,
          type: userType as string,
        });
        this.apiResponse = {
          type: 'success',
          message: this.$t('login.password_restore.yourPasswordHas'),
          linkText: this.$t('login.password_restore.Sign in'),
        };
      } catch (e) {
        this.apiResponse = {
          type: 'error',
          message: ((e as any).cause as any)?.response?.data?.message || 'Something went wrong - please try again later',
        };
      }
      this.loading = false;
    },

    login() {
      this.$router.push('/login');
    },
  },
});
</script>
